import { Zodiac } from "../api";

export const norm360 = (d: number) => ((d % 360) + 360) % 360;

export const toRad = (deg: number) => (-deg) * Math.PI / 180;

export const midAngle = (a: number, b: number) => {
  const aN = norm360(a);
  const bN = norm360(b);
  const delta = norm360(bN - aN);
  return norm360(aN + delta / 2);
};

export const signPositions: Record<Zodiac, number> = {
  Aries: 0,
  Taurus: 30,
  Gemini: 60,
  Cancer: 90,
  Leo: 120,
  Virgo: 150,
  Libra: 180,
  Scorpio: 210,
  Sagittarius: 240,
  Capricorn: 270,
  Aquarius: 300,
  Pisces: 330,
};

export function degToDMS(
  deg: number,
  { normalize = true, decimals = 0 } = {}
) {
  const d = normalize ? norm360(deg) : Math.abs(deg);
  let D = Math.floor(d);
  const Mfull = (d - D) * 60;
  let M = Math.floor(Mfull);
  let S = (Mfull - M) * 60;

  const k = Math.pow(10, decimals);
  S = Math.round(S * k) / k;

  const eps = 1e-9;
  if (S >= 60 - eps) { S = 0; M += 1; }
  if (M >= 60) { M = 0; D += 1; }
  if (normalize) D = D % 360;

  const sign = normalize ? 1 : (deg < 0 ? -1 : 1);
  return { sign, D, M, S };
}

const SUP: Record<string, string> = {
  "0": "⁰", "1": "¹", "2": "²", "3": "³", "4": "⁴", "5": "⁵", "6": "⁶", "7": "⁷", "8": "⁸", "9": "⁹"
};

function toSuperscript(n: number, pad = 2) {
  return String(n).padStart(pad, "0").replace(/\d/g, d => SUP[d]);
}

export function formatDMS(dms: { sign: number; D: number; M: number; S: number }) {
  const { sign, D, M, S } = dms;
  const s = S % 1 === 0 ? S.toFixed(0) : S.toString();
  const signChar = sign < 0 ? "−" : "";
  return `${signChar}${String(D).padStart(2, "0")}° ${String(M).padStart(2, "0")}′ ${s.padStart(2, "0")}″`;
}

export function formatDM(dms: { sign: number; D: number; M: number; S: number }) {
  const { sign, D, M } = dms;
  const signChar = sign < 0 ? "−" : "";
  return `${signChar}${String(D).padStart(2, "0")}° ${String(M).padStart(2, "0")}′`;
}

export function degToDMSString(deg: number, opts?: { normalize?: boolean; decimals?: number }) {
  return formatDMS(degToDMS(deg, opts));
}

export function degToDMString(deg: number, opts?: { normalize?: boolean; decimals?: number }) {
  return formatDM(degToDMS(deg, opts));
}

export function degToDMSCompactUnicode(deg: number) {
  const { D, M } = degToDMS(deg);
  return `${D}${toSuperscript(M)}`;
}